<template>
	<StoryblokComponent v-if="story" :blok="story.content" />
</template>

<script lang="ts" setup>
	import {useRouteParams} from '@vueuse/router';
	import {first, mapValues, valuesIn} from 'lodash-es';
	import {format} from 'date-fns';
	import articleHeroImages from '~/resources/articleHeroImages';
	import type {PageTypeRecipesRecipeDetailStoryblok, PageTypeRecipesRecipeListingStoryblok} from '@/storyblok/types';
	import type {BreadcrumbItem} from '~/types/breadcrumbs';

	definePageMeta({title: 'Recepty - detail | Globus'});

	const category = useRouteParams<string>('category');
	const slug = useRouteParams<string>('slug');
	const fullSlug = computed(() => `_globus-cz/recepty/_recepty/${category.value}/${slug.value}`);

	const [{data: story, error: storyError}, {data: categoryStory, error: categoryError}] = await Promise.all([
		useSingleStoryFetch<PageTypeRecipesRecipeDetailStoryblok>(fullSlug),
		useSingleStoryFetch<PageTypeRecipesRecipeListingStoryblok>(
			`category_${category.value}`,
			{
				starts_with: `_globus-cz/recepty/_recepty/${category.value}`,
				per_page: 1,
				is_startpage: true,
				content_type: 'PageTypeRecipesRecipeListing',
			},
		),
	]);

	if (!story.value || storyError.value || categoryError.value) {
		await tryDynamicRedirectOrShowError(storyError.value || categoryError.value);
	}

	const categoryName = first(valuesIn(mapValues(categoryStory.value, 'name')));

	const breadcrumbs = readonly<BreadcrumbItem[]>([
		{
			id: 0,
			parent_id: null,
			slug: 'recepty',
			name: 'Recepty',
		},
		{
			id: 1,
			parent_id: null,
			slug: `recepty/${category.value}`,
			name: categoryName ?? '',
		},
		{
			id: 2,
			parent_id: null,
			slug: `recepty/${category.value}/${slug.value}`,
			name: story.value?.content.title ?? '',
		},
	]);

	provide('articleHeroImage', articleHeroImages.recipe);
	provide('breadcrumbs', breadcrumbs);

	const focalFilter = computed(() => story.value?.content.image.focus ? `filters:focal(${story.value.content.image.focus})` : '');
	const jsonLd = {
		'@context': 'https://schema.org',
		'@type': 'Recipe',
		name: story.value?.content.title,
		image: [
			`${story.value?.content.image.filename}/m/1000x1000/${focalFilter.value}`,
			`${story.value?.content.image.filename}/m/1200x900/${focalFilter.value}`,
			`${story.value?.content.image.filename}/m/1600x900/${focalFilter.value}`,
		],
		author: {
			'@type': 'Person',
			// @ts-expect-error name je resolvnuty
			name: story.value?.content.author?.name ?? '',
		},
		datePublished: story.value?.content.dateOfPublication ? format(new Date(story.value.content.dateOfPublication), 'yyyy-MM-dd') : '',
		description: story.value?.content.perex,
		totalTime: `PT${story.value?.content.preparationTime}M`,
		recipeCategory: categoryName ?? '',
		recipeIngredient: story.value?.content.ingredients.flatMap((item: any) => item.listOfIngredients.map((ingredient: any) => `${ingredient.quantity} - ${ingredient.name}`)),
	};

	useHead({
		title: `${story.value?.content.title} | Globus`,
		script: [
			{
				type: 'application/ld+json',
				textContent: JSON.stringify(jsonLd),
			},
		],
	});

	useSeoSettings(story.value?.content.seoSettings?.[0]);
</script>
